import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import makeStyles from "@mui/styles/makeStyles";
import { AdminActionButtons } from "../component/secretSanta/admin/AdminActionButtons";
import { CollectingInstructions, DeliveryInstructions } from "../component/secretSanta/Instructions";
import InstructionTabPanel from "./InstructionTabPanel";
import { api } from "../../api/api";
import SantasStatusCounts from "../component/secretSanta/common/SantasStatusCounts";

const useStyles = makeStyles((theme) => ({
  detailRow: {
    display: "flex",
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: "bold",
    minWidth: "200px",
    marginRight: theme.spacing(2),
  },
  value: {
    flex: 1,
  },
  actionsRow: {
    display: "flex",
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    alignItems: "center",
  },
  actionsLabel: {
    fontWeight: "bold",
    marginRight: theme.spacing(2),
  },
  instructionsSection: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  instructionLabel: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
}));

export default function SecretSantaLocationDetailDialog(props) {
  const classes = useStyles();
  const { locationId, open, handleClose, setBackdropOpen, setReload } = props;
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogReload, setDialogReload] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [editingDelivery, setEditingDelivery] = useState(false);
  const [deliveryInstructionsEdit, setDeliveryInstructionsEdit] = useState("");
  const [editingCollecting, setEditingCollecting] = useState(false);
  const [collectingInstructionsEdit, setCollectingInstructionsEdit] = useState("");

  useEffect(() => {
    if (open && locationId) {
      setLoading(true);
      api.secretSanta.getLocation(locationId)
        .then((data) => {
          setLocation(data);
          setDeliveryInstructionsEdit(data.deliveryInstructions || "");
          setCollectingInstructionsEdit(data.collectingInstructions || "");
          setLoading(false);
          setDialogReload(false);
        })
        .catch((error) => {
          console.error("Failed to fetch location:", error);
          setLoading(false);
          setDialogReload(false);
        });
    }
  }, [open, locationId, dialogReload]);

  const handleDialogReload = (value) => {
    setReload(value);
    setDialogReload(value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEditDelivery = () => {
    setEditingDelivery(true);
  };

  const handleSaveDelivery = () => {
    setBackdropOpen(true);
    api.secretSanta.updateLocationInstructions(locationId, {
      deliveryInstructions: deliveryInstructionsEdit
    })
      .then(() => {
        setEditingDelivery(false);
        handleDialogReload(true);
        setBackdropOpen(false);
      })
      .catch((error) => {
        console.error("Failed to update delivery instructions:", error);
        setBackdropOpen(false);
      });
  };

  const handleCancelDelivery = () => {
    setDeliveryInstructionsEdit(location.deliveryInstructions || "");
    setEditingDelivery(false);
  };

  const handleEditCollecting = () => {
    setEditingCollecting(true);
  };

  const handleSaveCollecting = () => {
    setBackdropOpen(true);
    api.secretSanta.updateLocationInstructions(locationId, {
      collectingInstructions: collectingInstructionsEdit
    })
      .then(() => {
        setEditingCollecting(false);
        handleDialogReload(true);
        setBackdropOpen(false);
      })
      .catch((error) => {
        console.error("Failed to update collecting instructions:", error);
        setBackdropOpen(false);
      });
  };

  const handleCancelCollecting = () => {
    setCollectingInstructionsEdit(location.collectingInstructions || "");
    setEditingCollecting(false);
  };

  const handleActiveToggle = (event) => {
    const newActiveState = event.target.checked;
    setBackdropOpen(true);

    const apiCall = newActiveState
      ? api.secretSanta.activateLocation(locationId)
      : api.secretSanta.deactivateLocation(locationId);

    apiCall
      .then(() => {
        handleDialogReload(true);
        setBackdropOpen(false);
      })
      .catch((error) => {
        console.error("Failed to update location active state:", error);
        setBackdropOpen(false);
      });
  };

  if (!open) {
    return null;
  }

  if (loading || !location) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>{location.name}</span>
          <FormControlLabel
            control={
              <Switch
                checked={location.isActive}
                onChange={handleActiveToggle}
                color="primary"
              />
            }
            label={location.isActive ? "Active" : "Inactive"}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="location tabs">
          <Tab label="Base Info" />
          <Tab label="Delivery instructions" />
          <Tab label="Collecting instructions" />
        </Tabs>

        {tabValue === 0 && (
          <Box className={classes.tabPanel}>
            <Box className={classes.detailRow}>
              <Typography className={classes.label}>Santas count:</Typography>
              <Typography className={classes.value}>{location.santasCount}</Typography>
            </Box>
            <Box className={classes.detailRow}>
              <Typography className={classes.label}>Santas count by statuses:</Typography>
              <Typography className={classes.value}>
                <SantasStatusCounts santasCountByStatus={location.santasCountByStatus}
                                    santasCount={location.santasCount}
                />
              </Typography>
            </Box>
            <Box className={classes.detailRow}>
              <Typography className={classes.label}>Presents destination:</Typography>
              <Typography className={classes.value}>
                {location.lockersSupported ? `📦: ${location.exactAddress}` : "🎄"}
              </Typography>
            </Box>
            <Box className={classes.detailRow}>
              <Typography className={classes.label}>Reindeers:</Typography>
              <Typography className={classes.value}>
                {location.reindeers ? location.reindeers : "🤷‍♂️"}
              </Typography>
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          <Box className={classes.tabPanel}>
            <InstructionTabPanel
              InstructionComponent={DeliveryInstructions}
              compiledInstructions={location.deliveryInstructionsCompiled}
              rawInstructions={location.deliveryInstructions}
              editValue={deliveryInstructionsEdit}
              isEditing={editingDelivery}
              onEdit={handleEditDelivery}
              onSave={handleSaveDelivery}
              onCancel={handleCancelDelivery}
              onEditChange={(e) => setDeliveryInstructionsEdit(e.target.value)}
            />
          </Box>
        )}

        {tabValue === 2 && (
          <Box className={classes.tabPanel}>
            <InstructionTabPanel
              InstructionComponent={CollectingInstructions}
              compiledInstructions={location.collectingInstructionsCompiled}
              rawInstructions={location.collectingInstructions}
              isEditing={editingCollecting}
              editValue={collectingInstructionsEdit}
              onEdit={handleEditCollecting}
              onSave={handleSaveCollecting}
              onCancel={handleCancelCollecting}
              onEditChange={(e) => setCollectingInstructionsEdit(e.target.value)}
            />
          </Box>
        )}

        <Box className={classes.actionsRow}>
          <Typography className={classes.actionsLabel}>Actions:</Typography>
          {location.isActive
            && <AdminActionButtons
              locationId={location.id}
              locationName={location.name}
              lockersSupported={location.lockersSupported}
              santaState={location.santaState}
              setRequestSantaState={handleDialogReload}
              setBackdropOpen={setBackdropOpen}
            />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

